import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import Pagination, { PAGE_SIZE } from './Pagination';
import {
  RiAddLine, RiCheckLine, RiTimeLine, RiEditLine,
  RiDeleteBinLine, RiSaveLine, RiCloseLine, RiListCheck2,
  RiSearchLine,
} from 'react-icons/ri';

const TODO_STATUSES = ['pending', 'in_progress', 'completed'];

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     dot: '#9CA3AF', badge: 'badge text-[#6B7280] bg-[#F3F4F6] border-[#E5E7EB]' },
  in_progress: { label: 'In Progress', dot: '#0984e3', badge: 'badge text-[#0984e3] bg-[rgba(9,132,227,0.08)] border-[rgba(9,132,227,0.2)]' },
  completed:   { label: 'Completed',   dot: '#10b981', badge: 'badge text-[#10b981] bg-[rgba(16,185,129,0.08)] border-[rgba(16,185,129,0.2)]' },
};

const EMPTY_FORM = { title: '', description: '', due_date: '' };

function nextStatus(s) {
  if (s === 'pending') return 'in_progress';
  if (s === 'in_progress') return 'completed';
  return 'pending';
}

export default function PersonalTodosPanel() {
  const [todos,          setTodos]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [filter,         setFilter]         = useState('all');
  const [form,           setForm]           = useState(EMPTY_FORM);
  const [adding,         setAdding]         = useState(false);
  const [editingId,      setEditingId]      = useState(null);
  const [editForm,       setEditForm]       = useState(EMPTY_FORM);
  const [deletingId,     setDeletingId]     = useState(null);
  const [statusChanging, setStatusChanging] = useState(null);
  const [error,          setError]          = useState('');

  const [page,  setPage]  = useState(1);
  const [total, setTotal] = useState(0);
  const [search,        setSearch]        = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input by 300ms before firing the API call
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 when search changes
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = { page, limit: PAGE_SIZE };
    if (debouncedSearch) params.search = debouncedSearch;
    api.get('/todos', { params })
      .then((res) => {
        const d = res.data;
        const list = Array.isArray(d) ? d : d?.todos || d?.data || [];
        setTodos(list);
        setTotal(d?.meta?.total ?? list.length);
      })
      .catch(() => setError('Failed to load todos. Please try again.'))
      .finally(() => setLoading(false));
  }, [page, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.title.trim()) return;
    setAdding(true);
    setError('');
    try {
      const payload = { title: form.title.trim(), description: form.description.trim(), status: 'pending' };
      if (form.due_date) payload.due_date = form.due_date;
      const res = await api.post('/todos', payload);
      const created = res.data?.todo || res.data?.data || res.data;
      setTodos((prev) => [created, ...prev]);
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create todo.');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (t) => {
    setEditingId(t._id || t.id);
    setEditForm({
      title:       t.title || '',
      description: t.description || '',
      due_date:    (t.due_date || '').slice(0, 10),
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(EMPTY_FORM); };

  const handleEdit = async (id) => {
    if (!editForm.title.trim()) return;
    try {
      const payload = { title: editForm.title.trim(), description: editForm.description.trim() };
      if (editForm.due_date) payload.due_date = editForm.due_date;
      const res = await api.put(`/todos/${id}`, payload);
      const updated = res.data?.todo || res.data?.data || res.data;
      setTodos((prev) =>
        prev.map((t) => (t._id || t.id) === id ? (updated && typeof updated === 'object' ? updated : { ...t, ...payload }) : t)
      );
      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update todo.');
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/todos/${id}`);
      setTodos((prev) => prev.filter((t) => (t._id || t.id) !== id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete todo.');
    } finally {
      setDeletingId(null);
    }
  };

  const changeStatus = async (id, newStatus) => {
    setStatusChanging(id);
    try {
      await api.put(`/todos/${id}`, { status: newStatus });
      setTodos((prev) =>
        prev.map((t) => (t._id || t.id) === id ? { ...t, status: newStatus } : t)
      );
    } catch {
      setError('Failed to update status.');
    } finally {
      setStatusChanging(null);
    }
  };

  const formatDue = (raw) => {
    if (!raw) return null;
    return new Date(raw).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isOverdue = (t) =>
    t.status !== 'completed' && t.due_date && new Date(t.due_date) < new Date();

  const stats = {
    pending:     todos.filter((t) => t.status === 'pending').length,
    in_progress: todos.filter((t) => t.status === 'in_progress').length,
    completed:   todos.filter((t) => t.status === 'completed').length,
  };

  const displayed = filter === 'all' ? todos : todos.filter((t) => t.status === filter);

  return (
    <div className="space-y-5">

      {/* Add form */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] p-6 space-y-4">
        <div>
          <h3 className="text-[#1F2937] font-bold">Add New TODO</h3>
          <p className="text-[#9CA3AF] text-sm mt-0.5">Personal tasks — only visible to you.</p>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center justify-between">
            {error}
            <button onClick={() => setError('')} className="ml-3 flex-shrink-0 cursor-pointer"><RiCloseLine size={15} /></button>
          </div>
        )}

        <div className="space-y-3">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAdd()}
            placeholder="Todo title *"
            className="input-field"
          />
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description (optional)"
            className="input-field"
          />
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-[#9CA3AF] mb-1 ml-0.5">Due date <span className="italic">(optional)</span></label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="input-field w-full"
              />
            </div>
            <button
              onClick={handleAdd}
              disabled={adding || !form.title.trim()}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              style={{ padding: '0.6rem 1.2rem', fontSize: '13px' }}
            >
              {adding
                ? <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                : <RiAddLine size={15} />}
              Add TODO
            </button>
          </div>
        </div>
      </div>

      {/* Stats + search + filter */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {TODO_STATUSES.map((s) => (
            <div key={s} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: STATUS_CONFIG[s].dot }} />
              <span className="text-xs text-[#6B7280]">
                <span className="font-semibold text-[#1F2937]">{stats[s]}</span> {STATUS_CONFIG[s].label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Search */}
          <div className="relative">
            <RiSearchLine
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title…"
              className="input-field pl-8 pr-8 text-sm py-1.5 w-56"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-red-500 transition-colors cursor-pointer"
                title="Clear search"
              >
                <RiCloseLine size={14} />
              </button>
            )}
          </div>

          {/* Status filter pills */}
          <div className="flex items-center gap-1 bg-white border border-[#E5E7EB] rounded-xl p-1">
            {['all', ...TODO_STATUSES].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                  filter === s ? 'text-white' : 'text-[#6B7280] hover:text-[#0984e3]'
                }`}
                style={filter === s ? { background: 'linear-gradient(135deg,#0984e3 0%,#d500f9 100%)' } : {}}
              >
                {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-[#E5E7EB] rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.06)] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F3F4F6]">
          <h3 className="text-[#1F2937] font-bold">
            {filter === 'all' ? 'All TODOs' : `${STATUS_CONFIG[filter].label} TODOs`}
          </h3>
          <span className="badge">{filter === 'all' ? total : displayed.length}</span>
        </div>

        {loading ? (
          <div className="divide-y divide-[#F3F4F6]">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="p-5 flex items-center gap-4">
                <div className="w-5 h-5 bg-[#F3F4F6] rounded-full flex-shrink-0 animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-[#F3F4F6] rounded w-2/5 animate-pulse" />
                  <div className="h-3 bg-[#F3F4F6] rounded w-3/5 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : displayed.length === 0 ? (
          <div className="text-center py-14">
            <RiListCheck2 size={32} className="text-[#E5E7EB] mx-auto mb-3" />
            <p className="text-[#9CA3AF] text-sm font-medium">
              {debouncedSearch
                ? `No todos match "${debouncedSearch}".`
                : filter === 'all'
                  ? 'No todos yet.'
                  : `No ${STATUS_CONFIG[filter]?.label} todos.`}
            </p>
            {!debouncedSearch && filter === 'all' && (
              <p className="text-[#9CA3AF] text-xs mt-1">Add your first todo above.</p>
            )}
            {debouncedSearch && (
              <button
                onClick={() => setSearch('')}
                className="mt-3 text-[#0984e3] text-sm font-medium hover:underline cursor-pointer"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#F3F4F6]">
            {displayed.map((t) => {
              const id = t._id || t.id;
              const isEditing  = editingId === id;
              const isDeleting = deletingId === id;
              const isChanging = statusChanging === id;
              const overdue    = isOverdue(t);

              return (
                <div
                  key={id}
                  className={`p-5 hover:bg-[#FAFBFC] transition-colors ${t.status === 'completed' ? 'opacity-60' : ''}`}
                >
                  {isEditing ? (
                    <div className="space-y-3">
                      <input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="input-field text-sm"
                        autoFocus
                      />
                      <input
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        placeholder="Description"
                        className="input-field text-sm"
                      />
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="block text-xs text-[#9CA3AF] mb-1 ml-0.5">Due date <span className="italic">(optional)</span></label>
                          <input
                            type="date"
                            value={editForm.due_date}
                            onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                            className="input-field text-sm w-full"
                          />
                        </div>
                        <button
                          onClick={() => handleEdit(id)}
                          disabled={!editForm.title.trim()}
                          className="btn btn-primary flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ padding: '0.45rem 0.9rem', fontSize: '12px' }}
                        >
                          <RiSaveLine size={13} /> Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="btn flex items-center gap-1.5 text-[#6B7280]"
                          style={{ padding: '0.45rem 0.9rem', fontSize: '12px' }}
                        >
                          <RiCloseLine size={13} /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      {/* Circle status toggle */}
                      <button
                        onClick={() => changeStatus(id, nextStatus(t.status))}
                        disabled={isChanging}
                        className="mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all cursor-pointer hover:scale-110 disabled:cursor-not-allowed"
                        style={{
                          borderColor: STATUS_CONFIG[t.status]?.dot || '#9CA3AF',
                          background:  t.status === 'completed' ? STATUS_CONFIG.completed.dot : 'transparent',
                        }}
                        title={`Mark as ${STATUS_CONFIG[nextStatus(t.status)]?.label}`}
                      >
                        {isChanging ? (
                          <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" style={{ color: STATUS_CONFIG[t.status]?.dot }} />
                        ) : t.status === 'completed' ? (
                          <RiCheckLine size={11} className="text-white" />
                        ) : null}
                      </button>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold mb-0.5 ${t.status === 'completed' ? 'line-through text-[#9CA3AF]' : 'text-[#1F2937]'}`}>
                          {t.title}
                        </p>
                        {t.description && (
                          <p className="text-[#6B7280] text-xs leading-relaxed mb-1.5 line-clamp-2">{t.description}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={STATUS_CONFIG[t.status]?.badge || 'badge'}>
                            {STATUS_CONFIG[t.status]?.label || t.status}
                          </span>
                          {t.due_date && (
                            <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-500 font-semibold' : 'text-[#9CA3AF]'}`}>
                              <RiTimeLine size={11} />
                              {overdue ? 'Overdue · ' : 'Due '}{formatDue(t.due_date)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <select
                          value={t.status}
                          onChange={(e) => changeStatus(id, e.target.value)}
                          disabled={isChanging}
                          className="text-xs font-semibold rounded-lg px-2 py-1.5 border cursor-pointer focus:outline-none mr-1 transition-colors"
                          style={{
                            color:       STATUS_CONFIG[t.status]?.dot || '#6B7280',
                            borderColor: '#E5E7EB',
                            background:  '#fff',
                          }}
                        >
                          {TODO_STATUSES.map((s) => (
                            <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => startEdit(t)}
                          className="p-1.5 text-[#9CA3AF] hover:text-[#0984e3] hover:bg-[rgba(9,132,227,0.08)] rounded-lg transition-all cursor-pointer"
                          aria-label="Edit"
                        >
                          <RiEditLine size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(id)}
                          disabled={isDeleting}
                          className="p-1.5 text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer disabled:opacity-50"
                          aria-label="Delete"
                        >
                          {isDeleting
                            ? <span className="w-3.5 h-3.5 border border-red-400 border-t-transparent rounded-full animate-spin block" />
                            : <RiDeleteBinLine size={14} />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={page} total={total} onChange={setPage} />
      </div>
    </div>
  );
}
